import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangeExpectByRequest,
  CloseCommitmentRequest,
  ClosedCommitmentDto,
  CommitmentAnnotationDto,
  CommitmentCheckInRequest,
  OpenCommitmentDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { invalidateAroundBooks, markBooksStale, patchBookRows } from './prospectBookCache';
import { useCampaignStore } from '../../stores';

const COMMITMENTS_PATH = '/api/fundraising/commitments';

// Every change to a commitment runs one at a time, so each reads the token the last one wrote back.
const COMMITMENT_SCOPE = { id: 'fundraising-commitment' };

/** The commitment's token as the cached chase list holds it right now. */
const currentToken = (queryClient: QueryClient, id: string) =>
  queryClient.getQueryData<OpenCommitmentDto[]>(queryKeys.fundraising.openCommitments())?.find((c) => c.id === id)?.concurrencyVersion;

/**
 * The Fundraiser's chase list (Fundraising: Manage): Open Commitments, soonest expect-by first.
 */
export function useOpenCommitments() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.fundraising.openCommitments(),
    queryFn: () => apiJson<OpenCommitmentDto[]>(COMMITMENTS_PATH, { error: 'Failed to fetch the open commitments' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/**
 * Close one for good with a Fundraiser's reason. A 409 (already closed, or changed since the list
 * loaded) rejects with the server's message; either way the list refetches.
 */
export function useCloseCommitment() {
  const queryClient = useQueryClient();
  return useMutation({
    // Queued behind a check-in or date change on the same list, and sent with the token the list holds when
    // it goes out, so a close straight after one of those doesn't false-conflict.
    scope: COMMITMENT_SCOPE,
    mutationFn: ({ id, request }: { id: string; request: CloseCommitmentRequest }) => {
      const concurrencyVersion = currentToken(queryClient, id) ?? request.concurrencyVersion;
      return apiJson<ClosedCommitmentDto>(`${COMMITMENTS_PATH}/${id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { ...request, concurrencyVersion },
        // A JSON `null` body reaches here as null, so keep the optional chain.
        error: (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? "Couldn't close the commitment. Try again."),
      });
    },
    // No token to write back: a closed commitment leaves the list and can never be closed again. The close
    // returns the person's row as the book now reads it, which replaces theirs in every cached book.
    onSuccess: (closed) => {
      if (closed.row) patchBookRows(queryClient, [closed.row]);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.openCommitments() });
      // A person's page (their commitment and timeline) and the Open commitments tab's count — never the book,
      // whose Committed chip follows on its next read.
      void invalidateAroundBooks(queryClient);
      void markBooksStale(queryClient);
    },
  });
}

/**
 * A check-in or an expected-date change on one Open Commitment (Fundraising: Manage) — annotations, never a
 * state. Follows the concurrency-token recipe (apps/web/CLAUDE.md) without the optimistic patch: each change
 * sends the row's token as the chase list holds it when the request goes out, the server's fresh token (and
 * the row's annotations) are written back on success, and one row's changes queue behind each other, so a
 * quick second change never false-conflicts with the first. A refusal rejects with the server's message.
 */
function useAnnotateCommitment<V extends { id: string }>(
  request: (variables: V, concurrencyVersion: number) => { path: string; method: 'POST' | 'PUT'; body: unknown },
  fallback: string,
) {
  const queryClient = useQueryClient();
  const listKey = queryKeys.fundraising.openCommitments();
  return useMutation({
    scope: COMMITMENT_SCOPE,
    mutationFn: (variables: V) => {
      const { path, method, body } = request(variables, currentToken(queryClient, variables.id) ?? 0);
      return apiJson<CommitmentAnnotationDto>(`${COMMITMENTS_PATH}/${variables.id}/${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        json: body,
        // A JSON `null` body reaches here as null, so keep the optional chain.
        error: (error: ApiErrorBody | null) => new Error(error?.error ?? error?.message ?? fallback),
      });
    },
    onSuccess: (saved) =>
      queryClient.setQueryData<OpenCommitmentDto[]>(listKey, (rows) =>
        rows?.map((c) =>
          c.id === saved.id
            ? { ...c, expectBy: saved.expectBy, latestCheckIn: saved.latestCheckIn, concurrencyVersion: saved.concurrencyVersion }
            : c,
        ),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      // A person's page shows the commitment and its check-ins on the timeline. No book row or count shows them.
      void invalidateAroundBooks(queryClient, { summaries: false });
    },
  });
}

/** Log a check-in — a short note, who and when — on an Open Commitment. */
export function useCheckInCommitment() {
  return useAnnotateCommitment<{ id: string; note: string }>(
    ({ note }, concurrencyVersion) => ({
      path: 'check-ins',
      method: 'POST',
      body: { note, concurrencyVersion } satisfies CommitmentCheckInRequest,
    }),
    "Couldn't save the check-in. Try again.",
  );
}

/** Set or clear when to expect an Open Commitment; the list regroups on the new date. */
export function useChangeCommitmentExpectBy() {
  return useAnnotateCommitment<{ id: string; expectBy: string | null }>(
    ({ expectBy }, concurrencyVersion) => ({
      path: 'expect-by',
      method: 'PUT',
      body: { expectBy, concurrencyVersion } satisfies ChangeExpectByRequest,
    }),
    "Couldn't change the expected date. Try again.",
  );
}
